import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Drawer, DrawerContent, DrawerOverlay, Text, VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import useSheetHistory from '../../hooks/useSheetHistory';

// The theme's danger look at the phone's size: white on the red by day; at
// night, where white on the lighter red falls under 4.5:1, outlined in it.
export const DESTRUCTIVE = {
  bg: 'redesign.accent.red',
  _hover: { bg: 'redesign.accent.red' },
  _active: { opacity: 0.85 },
  _dark: {
    bg: 'transparent',
    border: '1px solid',
    borderColor: 'redesign.accent.red',
    color: 'redesign.accent.red',
    _hover: { bg: 'transparent' },
  },
};

/**
 * A question at the bottom of the screen, in thumb reach, instead of a
 * centred dialog: a title, an optional line of body text, the action and
 * Cancel. The back swipe answers Cancel.
 *
 * `onConfirm` runs only after the sheet's own history entry is gone, so it
 * can close another sheet or navigate straight away.
 */
export default function PhoneConfirmSheet({
  isOpen,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation('common');
  const [closing, setClosing] = useState(false);
  const confirmed = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setClosing(false);
      confirmed.current = false;
    }
  }, [isOpen]);

  const { close } = useSheetHistory('confirm', isOpen, {
    onPop: () => { if (!confirmed.current) onCancel?.(); },
  });

  const handleConfirm = async () => {
    // The button stays tappable while the sheet slides out; a second tap
    // must not run the action twice.
    if (confirmed.current) return;
    confirmed.current = true;
    setClosing(true);
    await close();
    onConfirm?.();
  };

  return (
    <Drawer
      isOpen={isOpen && !closing}
      placement="bottom"
      onClose={() => close()}
      autoFocus={false}
      returnFocusOnClose={false}
    >
      <DrawerOverlay />
      <DrawerContent
        bg="redesign.bg.surface"
        color="redesign.text"
        maxW="100vw"
        borderTopRadius="22px"
        // Landscape: clear of the sensor housing, as the other sheets are.
        pl="calc(env(safe-area-inset-left) + 16px)"
        pr="calc(env(safe-area-inset-right) + 16px)"
        pt="8px"
        pb="calc(env(safe-area-inset-bottom) + 16px)"
      >
        <Box w="36px" h="5px" borderRadius="3px" bg="redesign.text.faint" mx="auto" mb="16px" />
        <Text textStyle="phoneHeadline" color="redesign.heading" textAlign="center">{title}</Text>
        {body && (
          <Text textStyle="phoneMeta" color="phone.muted" textAlign="center" mt="6px">{body}</Text>
        )}
        <VStack spacing="8px" mt="20px" align="stretch">
          <Button
            variant="phonePrimary"
            {...(destructive ? DESTRUCTIVE : {})}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
          <Button variant="ghost" h="52px" fontSize="17px" color="redesign.text" onClick={() => close()}>
            {cancelLabel || t('actions.cancel')}
          </Button>
        </VStack>
      </DrawerContent>
    </Drawer>
  );
}
